import { useRef, useState } from "react";
import { UploadCloud, X, Plus, Loader2, Star, PlayCircle, Crop } from "lucide-react";
import api from "../lib/api";
import { img, isVideo } from "../lib/utils-app";
import { CropModal } from "./CropModal";
import { toast } from "sonner";

export function ImageUploader({ value = [], onChange, lang = "en", single = false, aspect = 4 / 3 }) {
  const images = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [drag, setDrag] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const coverRef = useRef(null);
  const es = lang === "es";

  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));
  const makeCover = (i) => {
    if (i === 0) return;
    const c = [...images];
    const [x] = c.splice(i, 1);
    c.unshift(x);
    onChange(c);
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (!list.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of list) {
      try { uploaded.push(await uploadOne(f)); }
      catch { toast.error(`${es ? "Falló" : "Failed"}: ${f.name}`); }
    }
    if (uploaded.length) {
      onChange(single ? [uploaded[0]] : [...images, ...uploaded]);
      toast.success(`${es ? "Subidas" : "Uploaded"} ${uploaded.length}`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pickCover = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCropSrc(URL.createObjectURL(f));
    if (coverRef.current) coverRef.current.value = "";
  };

  const onCropDone = async (blob) => {
    const s = cropSrc;
    setCropSrc(null);
    if (s) URL.revokeObjectURL(s);
    if (!blob) return;
    setUploading(true);
    try {
      const file = new File([blob], `cover-${Date.now()}.jpg`, { type: "image/jpeg" });
      const u = await uploadOne(file);
      onChange(single ? [u] : [u, ...images]);
      toast.success(es ? "Portada actualizada" : "Cover updated");
    } catch { toast.error("Error"); } finally { setUploading(false); }
  };

  const addUrl = () => {
    const u = url.trim();
    if (!u) return;
    onChange(single ? [u] : [...images, u]);
    setUrl("");
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (single) {
      const f = e.dataTransfer.files?.[0];
      if (f) setCropSrc(URL.createObjectURL(f));
    } else {
      handleFiles(e.dataTransfer.files);
    }
  };

  return (
    <div data-testid="image-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => (single ? coverRef.current?.click() : fileRef.current?.click())}
        className={`cursor-pointer rounded-md border-2 border-dashed px-4 py-6 flex flex-col items-center justify-center gap-1.5 text-center transition-colors ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
        data-testid="upload-dropzone"
      >
        {uploading ? <Loader2 size={22} className="animate-spin text-primary" /> : <UploadCloud size={22} className="text-primary" />}
        <span className="text-sm font-medium">
          {uploading ? (es ? "Subiendo…" : "Uploading…") : single ? (es ? "Sube y recorta la imagen" : "Upload & crop image") : (es ? "Sube imágenes o video" : "Upload images or video")}
        </span>
        <span className="text-xs text-muted-foreground">{es ? "Arrastra o haz clic (máx 30MB)" : "Drag or click (max 30MB)"}</span>
      </div>
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="upload-input" />
      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={pickCover} data-testid="cover-input" />

      <div className="flex gap-2 mt-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} placeholder={es ? "…o pega una URL" : "…or paste a URL"} className="flex-1 bg-background border border-border focus:border-primary px-3 py-2 text-sm outline-none" data-testid="upload-url-input" />
        <button type="button" onClick={addUrl} className="px-3 py-2 border border-border hover:border-primary text-sm flex items-center gap-1" data-testid="upload-url-add"><Plus size={14} /> {es ? "Añadir" : "Add"}</button>
        {!single && (
          <button type="button" onClick={() => coverRef.current?.click()} className="px-3 py-2 border border-border hover:border-primary text-sm flex items-center gap-1" data-testid="crop-cover-btn"><Crop size={14} /> {es ? "Portada" : "Cover"}</button>
        )}
      </div>

      {images.length > 0 && (
        <div className={`grid gap-2 mt-3 ${single ? "grid-cols-3" : "grid-cols-4 sm:grid-cols-5"}`} data-testid="upload-thumbs">
          {images.map((src, i) => (
            <div key={`${src}-${i}`} className="relative group aspect-square rounded-md overflow-hidden border border-border" data-testid={`upload-thumb-${i}`}>
              {isVideo(src) ? (
                <div className="w-full h-full bg-black grid place-items-center"><PlayCircle size={22} className="text-white/80" /><video src={img(src)} className="absolute inset-0 w-full h-full object-cover opacity-70" muted /></div>
              ) : (
                <img src={img(src)} alt="" className="w-full h-full object-cover" />
              )}
              {!single && i === 0 && <span className="absolute bottom-0 inset-x-0 bg-[#d9a441] text-[#241703] text-[0.55rem] label-caps text-center py-0.5">{es ? "Portada" : "Cover"}</span>}
              <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600" data-testid={`upload-remove-${i}`} aria-label="remove"><X size={13} /></button>
              {!single && i !== 0 && <button type="button" onClick={() => makeCover(i)} className="absolute top-1 left-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#d9a441] hover:text-[#241703]" title={es ? "Hacer portada" : "Make cover"} data-testid={`upload-cover-${i}`}><Star size={12} /></button>}
            </div>
          ))}
        </div>
      )}

      {cropSrc && (
        <CropModal
          src={cropSrc} aspect={aspect} lang={lang}
          onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
          onDone={onCropDone}
        />
      )}
    </div>
  );
}
